import { oc } from "@orpc/contract";
import { z } from "zod";
import { FileExtractionOutputFormatSchema } from "@erp/shared-types";
import { SchemaDefinitionResponseSchema } from "../core/schema-definitions.contract";

const UUIDSchema = z.string().uuid();

const FileExtractionRunSummarySchema = z.object({
  id: UUIDSchema,
  schemaDefinitionId: UUIDSchema,
  schemaDefinitionVersion: z.number().int().positive(),
  schemaDefinitionVersionId: UUIDSchema,
  documentId: UUIDSchema,
  documentFileName: z.string(),
  ocrResultId: UUIDSchema,
  outputFormat: FileExtractionOutputFormatSchema,
  status: z.enum(["pending", "processing", "completed", "failed"]),
  error: z.string().nullable(),
  userId: UUIDSchema,
  userName: z.string().nullable(),
  createdAt: z.date(),
});

const FileExtractionRunDetailSchema = FileExtractionRunSummarySchema.extend({
  documentMimeType: z.string().nullable(),
  userInstructions: z.string().nullable(),
  extractedResult: z.record(z.string(), z.unknown()).nullable(),
  formattedOutput: z.string(),
  formattedRows: z.array(z.record(z.string(), z.unknown())),
});

export const fileExtractionContracts = {
  listSchemas: oc
    .input(z.object({}))
    .output(z.array(SchemaDefinitionResponseSchema))
    .route({ method: "GET" }),

  listRuns: oc
    .input(
      z.object({
        schemaDefinitionId: UUIDSchema,
        limit: z.number().int().positive().max(200).optional(),
        offset: z.number().int().nonnegative().optional(),
      }),
    )
    .output(
      z.object({
        items: z.array(FileExtractionRunSummarySchema),
        total: z.number().int().nonnegative(),
      }),
    )
    .route({ method: "GET" }),

  getRun: oc
    .input(z.object({ runId: UUIDSchema }))
    .output(FileExtractionRunDetailSchema)
    .route({ method: "GET" }),

  run: oc
    .input(
      z.object({
        schemaDefinitionId: UUIDSchema,
        documentId: UUIDSchema,
        userInstructions: z.string().nullable().optional(),
        outputFormat: FileExtractionOutputFormatSchema.optional(),
      }),
    )
    .output(FileExtractionRunDetailSchema)
    .route({ method: "POST" }),
} as const;

export type FileExtractionRunSummary = z.infer<
  typeof FileExtractionRunSummarySchema
>;
export type FileExtractionRunDetail = z.infer<
  typeof FileExtractionRunDetailSchema
>;
